'use client';

import React, { useRef, useEffect } from 'react';
import Link from 'next/link';
import { ArrowRight, ChevronRight } from 'lucide-react';

interface Programme {
  id: string;
  title: string;
  label: string;
  description: string;
  duration?: string;
  tag?: string;
  tagColor?: string;
  comingSoon?: boolean;
}

interface School {
  id: string;
  name: string;
  description: string;
  colour: 'gold' | 'green' | 'slate' | 'emerald' | 'navy';
  programmes: Programme[];
}

const schools: School[] = [
  {
    id: 'property-practice',
    name: 'School of Property Practice',
    description:
      'The foundational school for estate agents, property practitioners, and built environment professionals — covering NQF-aligned qualifications, compliance, and professional practice.',
    colour: 'gold',
    programmes: [
      {
        id: 'q-001',
        title: 'Estate Agency & Property Practice',
        label: 'NQF Level 4 Qualification',
        description:
          'The foundational qualification for aspiring estate agents — covering property legislation, ethics, conveyancing basics, FICA compliance, and professional practice standards.',
        duration: '6 months',
        tag: 'Entry Pathway',
        tagColor: 'bg-accent/10 text-accent border border-accent/20',
      },
      {
        id: 'q-002',
        title: 'Principal Property Practitioner Programme',
        label: 'NQF Level 5 Qualification',
        description:
          'Advanced qualification for property professionals seeking principal status — covering business management, compliance, team leadership, and advanced property law.',
        duration: '10 months',
        tag: 'Professional',
        tagColor: 'bg-primary/10 text-primary border border-primary/20',
      },
      {
        id: 'q-003',
        title: 'Real Estate Leadership',
        label: 'NQF Level 6 Pathway',
        description:
          'A future-facing leadership qualification for senior real estate professionals — combining strategic management, investment analysis, and institutional leadership.',
        duration: '12 months',
        tag: 'Coming Soon',
        tagColor: 'bg-muted-foreground/10 text-muted-foreground border border-border',
        comingSoon: true,
      },
      {
        id: 'c-001',
        title: 'Facilities & Property Management',
        label: 'Industry Certificate',
        description:
          'Practical training in facilities management, building operations, lease administration, and property compliance for built environment professionals.',
        duration: '6 months',
      },
      {
        id: 'c-003',
        title: 'Property Marketing & Client Psychology',
        label: 'Industry Certificate',
        description:
          'Master the art of property marketing, digital strategy, and client psychology to build a high-performing sales and advisory practice.',
        duration: '4 months',
      },
    ],
  },
  {
    id: 'real-estate',
    name: 'School of Real Estate',
    description:
      'Advanced real estate education for professionals seeking to lead in luxury advisory, commercial strategy, investment, and the application of technology in property markets.',
    colour: 'green',
    programmes: [
      {
        id: 'e-001',
        title: 'Luxury Real Estate Advisory',
        label: 'Executive Certificate',
        description:
          'Master the psychology, strategy, and craft of advising high-net-worth clients in premium residential and commercial property markets.',
        duration: '8 months',
      },
      {
        id: 'e-003',
        title: 'Real Estate Investment & Portfolio Strategy',
        label: 'Executive Programme',
        description:
          'Build and manage high-performing property portfolios using institutional-grade investment analysis, deal structuring, and wealth-building strategies.',
        duration: '12 months',
      },
      {
        id: 'e-005',
        title: 'Artificial Intelligence & PropTech for Real Estate',
        label: 'Executive Technology Certificate',
        description:
          'Harness AI, machine learning, and PropTech tools to gain competitive advantage in valuations, market prediction, and data-driven investment decisions.',
        duration: '6 months',
        tag: 'New',
        tagColor: 'bg-accent/10 text-accent border border-accent/20',
      },
      {
        id: 'e-006',
        title: 'Commercial Real Estate Strategy',
        label: 'Executive Programme',
        description:
          'Manage commercial, retail, and industrial properties at an institutional level — covering lease structuring, asset management, and portfolio optimisation.',
        duration: '10 months',
      },
    ],
  },
  {
    id: 'property-development',
    name: 'School of Property Development',
    description:
      'Comprehensive programmes for development professionals — from strategic development and construction management to feasibility analysis and the full project lifecycle.',
    colour: 'slate',
    programmes: [
      {
        id: 'e-002',
        title: 'Strategic Property Development',
        label: 'Advanced Professional Programme',
        description:
          'A comprehensive programme covering the full property development lifecycle — from site acquisition and feasibility to construction management and portfolio exit.',
        duration: '18 months',
        tag: 'Most Comprehensive',
        tagColor: 'bg-accent/10 text-accent border border-accent/20',
      },
      {
        id: 'c-002',
        title: 'Construction & Development Management',
        label: 'Professional Certificate',
        description:
          'Covers the full construction and development management process — from project inception and procurement to site management and handover.',
        duration: '16 months',
      },
      {
        id: 'c-004',
        title: 'Development Feasibility Analysis',
        label: 'Industry Certificate',
        description:
          'Learn to conduct rigorous development feasibility studies — from site analysis and cost modelling to financial projections and risk assessment.',
        duration: '5 months',
      },
    ],
  },
  {
    id: 'property-finance',
    name: 'School of Property Finance',
    description:
      'Specialist finance education for property professionals — covering capital structuring, development finance, REIT analysis, and real estate investment strategy.',
    colour: 'navy',
    programmes: [
      {
        id: 'e-004',
        title: 'Real Estate Finance & Capital Structuring',
        label: 'Professional Certificate',
        description:
          'Deep-dive into property financing structures, development finance, REIT analysis, and capital markets — from South African banking practice to global property finance.',
        duration: '14 months',
      },
      {
        id: 'e-007',
        title: 'Urban Development & African Cities',
        label: 'Advanced Programme',
        description:
          'Explore the dynamics of urban growth, infrastructure investment, and real estate development across African cities and emerging markets.',
        duration: '12 months',
      },
    ],
  },
  {
    id: 'cities-infrastructure',
    name: 'School of Cities & Infrastructure',
    description:
      'Equipping leaders to shape the cities, communities, and infrastructure systems that drive economic growth, social inclusion, and sustainable development across Africa.',
    colour: 'emerald',
    programmes: [
      {
        id: 'ci-001',
        title: 'Spatial Planning & Land Use Management',
        label: 'Professional Programme',
        description:
          'Comprehensive grounding in SPLUMA, spatial development frameworks, land use rights, and municipal planning processes for built environment professionals.',
        duration: '8 months',
        tag: 'New Faculty',
        tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      },
      {
        id: 'ci-002',
        title: 'Urban Planning Fundamentals',
        label: 'Professional Certificate',
        description:
          'Town planning essentials for property professionals — covering rezoning, consent use, township establishment, subdivision, and planning approvals.',
        duration: '6 months',
        tag: 'New Faculty',
        tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      },
      {
        id: 'ci-003',
        title: 'Housing & Human Settlements',
        label: 'Professional Programme',
        description:
          'Practical knowledge in affordable housing development, student accommodation, human settlements planning, and housing policy delivery systems.',
        duration: '9 months',
        tag: 'New Faculty',
        tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      },
      {
        id: 'ci-005',
        title: 'Smart Cities & Future Development',
        label: 'Executive Technology Programme',
        description:
          'Explore smart city frameworks, PropTech innovation, artificial intelligence in urban development, and data-driven planning for the cities of tomorrow.',
        duration: '6 months',
        tag: 'New',
        tagColor: 'bg-accent/10 text-accent border border-accent/20',
      },
      {
        id: 'ci-007',
        title: 'African Cities & Urban Transformation',
        label: 'Advanced Programme',
        description:
          'Comparative study of African urban development, future cities, and regional development strategies — positioning professionals to lead transformation across the continent.',
        duration: '8 months',
        tag: 'New Faculty',
        tagColor: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      },
    ],
  },
];

const schoolStyles: Record<School['colour'], {
  separator: string;
  labelBg: string;
  labelBorder: string;
  dot: string;
  cardBorder: string;
  cardAccent: string;
  accentLine: string;
  badge: string;
}> = {
  gold: {
    separator: 'border-accent',
    labelBg: 'bg-accent/8 text-accent',
    labelBorder: 'border-accent/25',
    dot: 'bg-accent',
    cardBorder: 'border-border hover:border-accent/30',
    cardAccent: 'group-hover:bg-accent/5',
    accentLine: 'bg-accent',
    badge: 'bg-accent/10 text-accent border-accent/20',
  },
  green: {
    separator: 'border-primary',
    labelBg: 'bg-primary/8 text-primary',
    labelBorder: 'border-primary/25',
    dot: 'bg-primary',
    cardBorder: 'border-border hover:border-primary/30',
    cardAccent: 'group-hover:bg-primary/3',
    accentLine: 'bg-primary',
    badge: 'bg-primary/10 text-primary border-primary/20',
  },
  slate: {
    separator: 'border-muted-foreground/40',
    labelBg: 'bg-muted-foreground/8 text-muted-foreground',
    labelBorder: 'border-muted-foreground/20',
    dot: 'bg-muted-foreground/60',
    cardBorder: 'border-border hover:border-muted-foreground/30',
    cardAccent: 'group-hover:bg-secondary/80',
    accentLine: 'bg-muted-foreground/50',
    badge: 'bg-muted-foreground/10 text-muted-foreground border-muted-foreground/20',
  },
  navy: {
    separator: 'border-blue-700',
    labelBg: 'bg-blue-50 text-blue-800',
    labelBorder: 'border-blue-200',
    dot: 'bg-blue-700',
    cardBorder: 'border-border hover:border-blue-300',
    cardAccent: 'group-hover:bg-blue-50/40',
    accentLine: 'bg-blue-700',
    badge: 'bg-blue-50 text-blue-800 border-blue-200',
  },
  emerald: {
    separator: 'border-emerald-600',
    labelBg: 'bg-emerald-50 text-emerald-700',
    labelBorder: 'border-emerald-200',
    dot: 'bg-emerald-600',
    cardBorder: 'border-border hover:border-emerald-300',
    cardAccent: 'group-hover:bg-emerald-50/50',
    accentLine: 'bg-emerald-600',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
};

export default function FeaturedProgrammes() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.06 }
    );
    const els = ref?.current?.querySelectorAll('.section-reveal, .stagger-child');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="section-pad-lg bg-secondary" id="programmes" ref={ref}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">

        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6 section-reveal">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="divider-gold" />
              <span className="label-caps">Academic Programmes</span>
            </div>
            <h2 className="font-serif text-section-title text-foreground">
              REUSA Schools &amp;
              <br />
              <span className="italic">Academic Faculties</span>
            </h2>
            <p className="font-sans text-sm text-muted-foreground mt-4 max-w-xl leading-relaxed">
              Five specialist schools — each with a distinct academic focus, curated programmes, and a clear pathway from foundational qualifications to executive education.
            </p>
          </div>
          <Link
            href="/course-marketplace"
            className="flex items-center gap-2 font-sans text-sm font-semibold text-primary hover:text-accent transition-colors group hover-underline-gold">
            View All Programmes
            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Schools */}
        <div className="space-y-20">
          {schools.map((school, schoolIndex) => {
            const styles = schoolStyles[school.colour];
            return (
              <div key={school.id} className="stagger-child" style={{ transitionDelay: `${schoolIndex * 80}ms` }}>

                {/* School Separator & Header */}
                <div className="mb-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`h-px flex-1 border-t ${styles.separator} opacity-40`} />
                    <div className="flex items-center gap-3">
                      <div className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} />
                      <span className={`font-sans text-[0.65rem] font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full border ${styles.labelBg} ${styles.labelBorder}`}>
                        Faculty
                      </span>
                      <div className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} />
                    </div>
                    <div className={`h-px flex-1 border-t ${styles.separator} opacity-40`} />
                  </div>
                  <div className="text-center max-w-2xl mx-auto">
                    <h3 className="font-serif text-2xl lg:text-3xl font-semibold text-foreground mb-3">
                      {school.name}
                    </h3>
                    <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                      {school.description}
                    </p>
                  </div>
                </div>

                {/* Programme Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {school.programmes.map((prog, progIndex) => (
                    <div
                      key={prog.id}
                      className={`bg-card rounded-xl border ${styles.cardBorder} transition-all duration-300 group cursor-pointer overflow-hidden ${prog.comingSoon ? 'opacity-70' : ''}`}
                      style={{ transitionDelay: `${progIndex * 50}ms` }}>

                      {/* Top accent line on hover */}
                      <div className={`h-0.5 w-full ${styles.accentLine} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                      <div className={`p-6 transition-colors duration-300 ${styles.cardAccent}`}>

                        {/* Label row */}
                        <div className="flex items-start justify-between gap-2 mb-4">
                          <span className={`font-sans text-[0.6rem] font-bold tracking-[0.12em] uppercase px-2.5 py-1 rounded-md border ${styles.labelBg} ${styles.labelBorder}`}>
                            {prog.label}
                          </span>
                          {prog.tag && (
                            <span className={`font-sans text-[0.6rem] font-bold px-2.5 py-1 rounded-md ${prog.tagColor}`}>
                              {prog.tag}
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <h4 className="font-serif font-semibold text-base lg:text-lg text-foreground mb-2 leading-snug group-hover:text-primary transition-colors duration-200">
                          {prog.title}
                        </h4>

                        {/* Duration */}
                        {prog.duration && (
                          <p className="font-sans text-[0.7rem] text-muted-foreground mb-3 font-medium">
                            {prog.duration}
                          </p>
                        )}

                        {/* Description */}
                        <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-5 line-clamp-3">
                          {prog.description}
                        </p>

                        {/* CTA */}
                        <div className="flex items-center justify-between pt-4 border-t border-border/60">
                          {prog.comingSoon ? (
                            <span className="font-sans text-xs text-muted-foreground italic">Available 2026</span>
                          ) : (
                            <Link
                              href="/course-marketplace"
                              className="flex items-center gap-1.5 font-sans text-xs font-semibold text-primary hover:text-accent transition-colors group/link">
                              View Programme
                              <ArrowRight size={12} className="group-hover/link:translate-x-0.5 transition-transform" />
                            </Link>
                          )}
                          <Link
                            href="/apply"
                            className="font-sans text-xs font-semibold text-muted-foreground hover:text-accent transition-colors">
                            Apply Now →
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 pt-12 border-t border-border/40 flex flex-col sm:flex-row items-center justify-between gap-6 section-reveal">
          <div>
            <p className="font-serif text-lg text-foreground font-medium">Not sure which programme is right for you?</p>
            <p className="font-sans text-sm text-muted-foreground mt-1">Speak with an academic advisor to find your pathway.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/course-marketplace" className="btn-outline px-6 py-3 rounded-xl text-sm font-semibold">
              Browse All Programmes
            </Link>
            <Link href="/apply" className="btn-gold px-6 py-3 rounded-xl text-sm font-semibold">
              Apply Now
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}
